import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import './QuestionBank.css';
import { getUser } from '../utils/authUtil';

const DIFFICULTY_COLORS = {
  easy:   { bg: '#e6f9f0', color: '#1a7f3c' },
  medium: { bg: '#fff8e6', color: '#b37400' },
  hard:   { bg: '#fff0f0', color: '#cc0000' },
};

const CATEGORY_COLORS = {
  quantitative: '#0f3460',
  logical:      '#8e44ad',
  verbal:       '#16a085',
  programming:  '#e94560',
  sql:          '#f39c12',
};

const emptyForm = {
  question_text: '', topic_id: '', difficulty: 'medium',
  correct_answer: '', explanation: '',
  options: ['', '', '', ''],
  source: 'manual', tags: '',
  company_id: ''
};

const QuestionBank = () => {
  const navigate = useNavigate();
  const [user] = useState(getUser() || {});
  const [questions, setQuestions] = useState([]);
  const [metadata, setMetadata] = useState({ total: 0, page: 1, total_pages: 1 });
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ topic_id: '', difficulty: '', search: '', company_id: '', ownership: '', college_id: '', page: 1 });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [allColleges, setAllColleges] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    fetchTopics();
    fetchCompanies();
    fetchQuestions();
    if (isSuperAdmin) fetchColleges();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  fetchQuestions();
}, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTopics = async () => {
    try {
      const res = await API.get('/questions/topics?includeEmpty=true');
      setTopics(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchCompanies = async () => {
    try {
      const res = await API.get('/questions/companies');
      setCompanies(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchColleges = async () => {
    try {
      const res = await API.get('/colleges');
      setAllColleges(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      const res = await API.get('/questions', { params });
      setQuestions(res.data.data);
      setMetadata(res.data.metadata);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };


  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
    setSelectedQuestion(null);
  };

  const openEdit = async (id) => {
    try {
      const res = await API.get(`/questions/${id}`);
      const q = res.data;
      const opts = (q.options || []).length > 0
        ? (typeof q.options[0] === 'string' ? q.options : q.options.map(o => o.option_text || o))
        : ['', '', '', ''];
      setForm({
        question_text: q.question_text,
        topic_id: q.topic_id || '',
        difficulty: q.difficulty || 'medium',
        correct_answer: q.correct_answer || opts[0] || '',
        explanation: q.explanation || '',
        options: opts,
        source: q.source || 'manual',
        tags: q.tags ? q.tags.filter(t => !t.startsWith('Company:')).join(', ') : '',
        company_id: q.company_id || ''
      });
      setEditingId(id);
      setShowForm(true);
      setFormError('');
      setFormSuccess('');
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (!form.question_text.trim()) return setFormError('Question text is required');
    if (!form.topic_id) return setFormError('Please select a topic');
    if (form.options.some(o => !o.trim())) return setFormError('All 4 options are required');
    if (!form.correct_answer) return setFormError('Please select the correct answer');

    try {
      const tagsArray = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const companyTags = (form.selectedCompanies || []).map(c => `Company: ${c}`);

      const payload = {
        question_text: form.question_text.trim(),
        topic_id: form.topic_id,
        difficulty: form.difficulty,
        correct_answer: form.correct_answer,
        explanation: form.explanation?.trim() || null,
        options: form.options,
        source: form.source || 'manual',
        tags: tagsArray,
        company_id: form.company_id || null
      };
      if (editingId) {
        await API.put(`/questions/${editingId}`, payload);
        setFormSuccess('Question updated successfully!');
      } else {
        await API.post('/questions', payload);
        setFormSuccess('Question added successfully!');
      }
      fetchQuestions();
      setTimeout(() => { setShowForm(false); setForm(emptyForm); }, 1200);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save question');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);

    try {
      const res = await API.post('/questions/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { message, summary, errors } = res.data;
      setBulkResults({ message, summary, errors: errors || [] });
      setShowBulkModal(true);
      fetchQuestions();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await API.delete(`/questions/${id}`);
      fetchQuestions();
      if (selectedQuestion?.id === id) setSelectedQuestion(null);
    } catch (err) { alert('Failed to delete'); }
  };

  const openPreview = async (id) => {
    try {
      const res = await API.get(`/questions/${id}`);
      setSelectedQuestion(res.data);
      setShowForm(false);
    } catch (err) { console.error(err); }
  };

  const groupedByCategory = topics.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const topicMap = {};
  topics.forEach(t => { topicMap[t.id] = t; });

  const categoryCounts = {};
  questions.forEach(q => {
    const cat = q.category || 'other';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  return (
    <>
      <div className="qb-page-content">

        <div className="top-bar">
  <div>
    <h2>Question Bank</h2>
    <p>{questions.length} questions total</p>
  </div>
  <div style={{ display: 'flex', gap: 10 }}>
    <button className="btn-bulk" onClick={() => document.getElementById('bulkInput').click()}>
      ⬆ Bulk Upload
    </button>
    <input id="bulkInput" type="file" accept=".xlsx,.xls,.csv"
      style={{ display: 'none' }}
      onChange={handleBulkUpload}
    />
    <button className="btn-add" onClick={openAdd}>+ Add Question</button>
    <button className="btn-bulk" style={{ background: '#f8fafc', color: '#0f172a', border: '1px solid #cbd5e1' }} onClick={() => navigate('/admin/topics')}>
      📚 Manage Topics
    </button>
  </div>
</div>


        <div className="qb-filters">
          <input className="qb-search" placeholder="Search questions..."
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} />

          <select className="qb-select" value={filters.topic_id}
            onChange={e => setFilters(p => ({ ...p, topic_id: e.target.value }))}>
            <option value="">All Topics</option>
            {Object.entries(groupedByCategory).map(([cat, tlist]) => (
              <optgroup key={cat} label={cat.toUpperCase()}>
                {tlist.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </optgroup>
            ))}
          </select>

          <select className="qb-select" value={filters.difficulty}
            onChange={e => setFilters({ ...filters, difficulty: e.target.value, page: 1 })}>
            <option value="">All Difficulty</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <select className="qb-select" value={filters.company_id}
            onChange={e => setFilters({ ...filters, company_id: e.target.value, page: 1 })}>
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select className="qb-select" value={filters.ownership}
            onChange={e => setFilters(p => ({ ...p, ownership: e.target.value }))}>
            <option value="">All Source</option>
            <option value="global">Global Questions</option>
            <option value="college">College Questions</option>
          </select>

          {isSuperAdmin && (
            <select className="qb-select" value={filters.college_id}
              onChange={e => setFilters(p => ({ ...p, college_id: e.target.value }))}>
              <option value="">All Colleges</option>
              {allColleges.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          {(filters.topic_id || filters.difficulty || filters.search || filters.ownership || filters.college_id) && (
            <button className="clear-btn"
              onClick={() => setFilters({ topic_id: '', difficulty: '', search: '', ownership: '', college_id: '', page: 1 })}>
              Clear
            </button>
          )}
        </div>

        <div className="qb-layout">
          {/* Question list */}
          <div className="qb-list">
            {loading ? (
              <div className="loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="qb-empty">
                <div className="qb-empty-icon">?</div>
                <p>No questions yet</p>
                <p className="qb-empty-sub">Click "+ Add Question" to add your first question</p>
              </div>
            ) : (
              questions.map((q, i) => {
                const catColor = CATEGORY_COLORS[q.category] || '#ccc';
                const diff = DIFFICULTY_COLORS[q.difficulty] || DIFFICULTY_COLORS.medium;
                return (
                  <div key={q.id} className={`qb-item ${selectedQuestion?.id === q.id ? 'active' : ''}`}
                    onClick={() => openPreview(q.id)}>
                    <div className="qb-item-top">
                      <div className="qb-num">{i + 1}</div>
                      <div className="qb-item-body">
                        <div className="qb-question-text">{q.question_text}</div>
                        <div className="qb-item-meta">
                          <span className="qb-topic" style={{ background: catColor + '18', color: catColor }}>
                            {q.topic_name || 'No topic'}
                          </span>
                          <span className="qb-diff" style={{ background: diff.bg, color: diff.color }}>
                            {q.difficulty}
                          </span>
                          {q.created_by_role === 'super_admin' ? (
                            <span className="qb-ownership global" title="Global Pool">🌍 Global Pool</span>
                          ) : (
                            <span className="qb-ownership college" title="College Specific">🏫 College Specific</span>
                          )}
                          {q.tags && q.tags.some(t => t.startsWith('Company:')) && (
                            <span className="qb-company">🏢 {q.tags.find(t => t.startsWith('Company:')).replace('Company: ', '')}</span>
                          )}
                          <span className="qb-source">👤 {q.added_by_name}</span>
                        </div>
                      </div>
                      <div className="qb-item-actions" onClick={e => e.stopPropagation()}>
                        {(isSuperAdmin || q.college_id === user.college_id) && (
                          <>
                            <button className="btn-edit-sm" onClick={() => openEdit(q.id)}>Edit</button>
                            <button className="btn-del-sm"  onClick={() => handleDelete(q.id)}>Del</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Pagination Controls */}
            {metadata.total_pages > 1 && (
              <div className="qb-pagination">
                <button 
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}
                  className="page-btn"
                >
                  ← Previous
                </button>
                <span className="page-info">
                  Page {metadata.page} of {metadata.total_pages}
                </span>
                <button 
                  disabled={filters.page >= metadata.total_pages}
                  onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}
                  className="page-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </div>

          {/* Right panel — Add/Edit form OR Preview */}
          <div className="qb-right">

            {showForm && (
              <div className="qb-form-card">
                <div className="qb-form-header">
                  <h3>{editingId ? 'Edit Question' : 'Add New Question'}</h3>
                  <button className="icon-close" onClick={() => setShowForm(false)}>×</button>
                </div>

                {formError   && <div className="alert error">{formError}</div>}
                {formSuccess && <div className="alert success">{formSuccess}</div>}

                <form onSubmit={handleSubmit}>
                  <div className="fg">
                    <label>Question Text *</label>
                    <textarea rows={3} placeholder="Enter the question..."
                      value={form.question_text}
                      onChange={e => setForm(p => ({ ...p, question_text: e.target.value }))} />
                  </div>

                  <div className="form-row">
                    <div className="fg">
                      <label>Topic *</label>
                      <select value={form.topic_id}
                        onChange={e => setForm(p => ({ ...p, topic_id: e.target.value }))}>
                        <option value="">Select topic</option>
                        {Object.entries(groupedByCategory).map(([cat, tlist]) => (
                          <optgroup key={cat} label={cat.toUpperCase()}>
                            {tlist.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="fg">
                      <label>Difficulty *</label>
                      <select value={form.difficulty}
                        onChange={e => setForm(p => ({ ...p, difficulty: e.target.value }))}>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="fg">
                      <label>Source</label>
                      <select value={form.source}
                        onChange={e => setForm(p => ({ ...p, source: e.target.value }))}>
                        <option value="manual">Manual (Admin Created)</option>
                        <option value="generated">AI Generated</option>
                      </select>
                    </div>
                  </div>

                  <div className="fg">
                    <label>Tags (comma separated)</label>
                    <input type="text" placeholder="Logic, Arithmetic, 2024..."
                      value={form.tags}
                      onChange={e => setForm({ ...form, tags: e.target.value })} />
                  </div>

                  <div className="fg">
                    <label>Target Company (Optional)</label>
                    <select value={form.company_id}
                      onChange={e => setForm({...form, company_id: e.target.value})}>
                      <option value="">No Specific Company</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="fg">
                    <label>Options (A, B, C, D) * — Click letter to mark as correct</label>
                    {form.options.map((opt, i) => (
                      <div key={i} className="option-row">
                        <div
                          className={`opt-label ${form.correct_answer === opt && opt ? 'correct' : ''}`}
                          onClick={() => opt.trim() && setForm(p => ({ ...p, correct_answer: opt }))}
                          title="Click to mark as correct answer">
                          {String.fromCharCode(65 + i)}
                        </div>
                        <input placeholder={`Option ${String.fromCharCode(65 + i)}`}
                          value={opt}
                          onChange={e => {
                            const opts = [...form.options];
                            // If this option was the correct answer, update its text
                            const wasCorrect = form.correct_answer === opts[i];
                            opts[i] = e.target.value;
                            setForm(p => ({
                              ...p,
                              options: opts,
                              correct_answer: wasCorrect ? e.target.value : p.correct_answer
                            }));
                          }} />
                        {form.correct_answer === opt && opt && (
                          <span className="correct-tick">✓ Correct</span>
                        )}
                      </div>
                    ))}
                    <div className="opt-hint">Click the letter (A/B/C/D) to mark as the correct answer</div>
                    {form.correct_answer && (
                      <div style={{ fontSize: 12, color: '#1a7f3c', marginTop: 4 }}>✓ Correct answer set: "{form.correct_answer}"</div>
                    )}
                  </div>

                  <div className="fg">
                    <label>Explanation (optional)</label>
                    <textarea rows={2} placeholder="Solution walkthrough..."
                      value={form.explanation}
                      onChange={e => setForm(p => ({ ...p, explanation: e.target.value }))} />
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel"
                      onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-submit">
                      {editingId ? 'Update Question' : 'Add Question'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {selectedQuestion && !showForm && (
              <div className="qb-preview-card">
                <div className="qb-form-header">
                  <h3>Question Preview</h3>
                  <button className="icon-close" onClick={() => setSelectedQuestion(null)}>×</button>
                </div>

                <div className="preview-meta">
                  <span className="qb-topic"
                    style={{
                      background: (CATEGORY_COLORS[selectedQuestion.category] || '#888') + '18',
                      color: CATEGORY_COLORS[selectedQuestion.category] || '#888'
                    }}>
                    {selectedQuestion.topic_name || 'No topic'}
                  </span>
                  <span className="qb-diff"
                    style={{
                      background: (DIFFICULTY_COLORS[selectedQuestion.difficulty] || DIFFICULTY_COLORS.easy).bg,
                      color: (DIFFICULTY_COLORS[selectedQuestion.difficulty] || DIFFICULTY_COLORS.easy).color
                    }}>
                    {selectedQuestion.difficulty}
                  </span>
                </div>

                <div className="preview-question">{selectedQuestion.question_text}</div>

                <div className="preview-options">
                  {selectedQuestion.options?.map((opt, i) => {
                    const optText = typeof opt === 'string' ? opt : opt.option_text;
                    const isCorrect = optText === selectedQuestion.correct_answer;
                    return (
                      <div key={i} className={`preview-option ${isCorrect ? 'correct' : ''}`}>
                        <span className="preview-opt-letter">{String.fromCharCode(65 + i)}</span>
                        <span>{optText}</span>
                        {isCorrect && <span className="preview-correct-badge">✓ Correct</span>}
                      </div>
                    );
                  })}
                </div>

                {selectedQuestion.explanation && (
                  <div className="preview-explanation">
                    <div className="exp-label">Explanation</div>
                    <div className="exp-text">{selectedQuestion.explanation}</div>
                  </div>
                )}

                <div className="preview-actions">
                  <button className="btn-edit-full" onClick={() => openEdit(selectedQuestion.id)}>
                    Edit Question
                  </button>
                  <button className="btn-del-full" onClick={() => handleDelete(selectedQuestion.id)}>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {!showForm && !selectedQuestion && (
              <div className="qb-right-empty">
                <div style={{ fontSize: 40, marginBottom: 12, color: '#ddd' }}>?</div>
                <p>Select a question to preview</p>
                <p style={{ fontSize: 12, color: '#bbb', marginTop: 6 }}>or click "+ Add Question"</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showBulkModal && bulkResults && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-box bulk-results-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Upload Results</h3>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="bulk-summary">
                <div className="summary-card added">
                  <div className="summary-val">{bulkResults.summary?.added || 0}</div>
                  <div className="summary-label">Added</div>
                </div>
                <div className="summary-card skipped">
                  <div className="summary-val">{bulkResults.summary?.skipped || 0}</div>
                  <div className="summary-label">Skipped</div>
                </div>
                <div className="summary-card total">
                  <div className="summary-val">{bulkResults.summary?.total || 0}</div>
                  <div className="summary-label">Total processed</div>
                </div>
              </div>

              {bulkResults.errors && bulkResults.errors.length > 0 && (
                <div className="bulk-errors">
                  <h4>Errors ({bulkResults.errors.length})</h4>
                  <div className="error-list">
                    {bulkResults.errors.map((err, idx) => (
                      <div key={idx} className="error-item">
                        <span className="err-row">Row {err.row}:</span>
                        <span className="err-msg">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-submit" onClick={() => setShowBulkModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionBank;